import React, { useMemo, useState } from "react";
import { useGSheet } from "@/hooks/useGSheet";
import { LiveHeader, StateBlock, Badge, crc, cell, fmtDate, phone } from "@/components/live/LiveShared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search, Loader2, SlidersHorizontal, Ban, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const num = (v) => Number(v || 0);

export default function LiveOrdersPage() {
  const { data, loading, error, refresh } = useGSheet("orders");
  const { data: orderItems, refresh: refreshItems } = useGSheet("orderItems");
  const { data: promotions } = useGSheet("promotions");

  const [search, setSearch] = useState("");
  const [adjustOrder, setAdjustOrder] = useState(null);
  const [linePrices, setLinePrices] = useState({});
  const [selectedPromotions, setSelectedPromotions] = useState([]);
  const [shippingAmount, setShippingAmount] = useState("0");
  const [sameBase, setSameBase] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [rollbackOrder, setRollbackOrder] = useState(null);
  const [rollbackConfirmText, setRollbackConfirmText] = useState("");
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);

  const rows = useMemo(() => {
    const s = search.toLowerCase();
    return data.filter((o) =>
      `${o.order_id} ${o.order_customer_name} ${o.customer_phone} ${o.source_channel}`.toLowerCase().includes(s)
    );
  }, [data, search]);

  const currentItems = useMemo(() => {
    if (!adjustOrder) return [];
    return orderItems.filter((item) => String(item.order_id) === String(adjustOrder.order_id));
  }, [orderItems, adjustOrder]);

  const subtotalPreview = useMemo(() => {
    return currentItems.reduce((sum, item) => {
      const itemId = String(item.order_item_id);
      const price = linePrices[itemId] === undefined ? num(item.unit_price) : num(linePrices[itemId]);
      return sum + price * num(item.qty);
    }, 0);
  }, [currentItems, linePrices]);

  const promotionPreview = useMemo(() => {
    const selected = promotions
      .filter((p) => selectedPromotions.includes(String(p.promotion_id)))
      .sort((a, b) => num(a.calculation_order) - num(b.calculation_order));

    let running = subtotalPreview;
    let discount = 0;
    const details = [];

    selected.forEach((p) => {
      const base = sameBase ? subtotalPreview : running;
      let amount = 0;
      if (p.discount_type === "percentage") amount = base * (num(p.discount_value) / 100);
      if (p.discount_type === "fixed") amount = Math.min(num(p.discount_value), base);
      amount = Math.round(amount * 100) / 100;
      discount += amount;
      if (!sameBase) running = Math.max(running - amount, 0);
      details.push({ ...p, base, amount });
    });

    const shipping = Math.max(num(shippingAmount), 0);
    const total = Math.round((Math.max(subtotalPreview - discount, 0) + shipping) * 100) / 100;
    return { discount, total, details };
  }, [promotions, selectedPromotions, subtotalPreview, shippingAmount, sameBase]);

  const orderTotal = (o) => (o.total_amount !== "" && o.total_amount != null ? o.total_amount : o.TOTAL);

  const openAdjust = (order) => {
    const items = orderItems.filter((item) => String(item.order_id) === String(order.order_id));
    const prices = {};
    items.forEach((item) => {
      prices[String(item.order_item_id)] = String(item.unit_price ?? "");
    });
    setAdjustOrder(order);
    setLinePrices(prices);
    setSelectedPromotions([]);
    setShippingAmount(String(order.shipping_amount || 0));
    setSameBase(false);
    setNote("");
  };

  const closeAdjust = () => {
    if (submitting) return;
    setAdjustOrder(null);
  };

  const togglePromotion = (id) => {
    setSelectedPromotions((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const submitAdjustment = async () => {
    if (!adjustOrder) return;

    const lineUpdates = currentItems.map((item) => ({
      order_item_id: String(item.order_item_id),
      unit_price: num(linePrices[String(item.order_item_id)]),
    }));

    if (lineUpdates.some((line) => !Number.isFinite(line.unit_price) || line.unit_price <= 0)) {
      toast.error("Todos los precios vendidos deben ser mayores que cero.");
      return;
    }

    setSubmitting(true);
    try {
      const { data: result } = await api.post("/gsheet/adjust-order", {
        order_id: String(adjustOrder.order_id),
        line_updates: lineUpdates,
        promotion_ids: selectedPromotions,
        shipping_amount: Math.max(num(shippingAmount), 0),
        promotion_mode: sameBase ? "same_base" : "sequential",
        note,
      });

      toast.success(`Orden #${adjustOrder.order_id} ajustada: total ${crc(result.totalAmount)}`);
      setAdjustOrder(null);
      await Promise.all([refresh(), refreshItems()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo ajustar la orden.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCancelOrder = async () => {
    if (!cancelOrder) return;
    const wasAlreadyCancelled = String(cancelOrder.order_status).toLowerCase() === "cancelada";
    setLifecycleSubmitting(true);
    try {
      await api.post("/gsheet/move-stage", {
        order_id: String(cancelOrder.order_id),
        stage: "Canceladas",
      });
      toast.success(
        wasAlreadyCancelled
          ? `Orden #${cancelOrder.order_id} normalizada: pago sin efecto y saldo en cero.`
          : `Orden #${cancelOrder.order_id} cancelada. Inventario restaurado y orden conservada en historial.`
      );
      setCancelOrder(null);
      await Promise.all([refresh(), refreshItems()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cancelar la orden.");
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  const openRollback = (order) => {
    setRollbackOrder(order);
    setRollbackConfirmText("");
  };

  const closeRollback = () => {
    if (lifecycleSubmitting) return;
    setRollbackOrder(null);
    setRollbackConfirmText("");
  };

  const submitRollback = async () => {
    if (!rollbackOrder) return;
    const orderId = String(rollbackOrder.order_id);
    if (rollbackConfirmText.trim() !== orderId) {
      toast.error(`Escribe ${orderId} para confirmar la eliminación.`);
      return;
    }

    setLifecycleSubmitting(true);
    try {
      await api.post("/gsheet/rollback-order", { order_id: orderId });
      toast.success(`Orden #${orderId} eliminada mediante rollback.`);
      setRollbackOrder(null);
      setRollbackConfirmText("");
      await Promise.all([refresh(), refreshItems()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo eliminar la orden de prueba.");
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <LiveHeader
        title="Órdenes (en vivo)"
        subtitle="Orders_MASTER desde Google Sheets"
        count={data.length}
        loading={loading}
        onRefresh={refresh}
        testid="live-orders"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar orden / cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid="orders-search" />
        </div>
      </LiveHeader>

      <StateBlock loading={loading} error={error} empty={!loading && !error && rows.length === 0} onRetry={refresh}>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left">
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3">Orden</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Teléfono</th>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3">Entrega</th>
                <th className="px-4 py-3">Pago</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Saldo</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((o, i) => {
                const isCancelled = String(o.order_status).toLowerCase() === "cancelada";
                const isFinanciallyVoided = String(o.payment_status).toUpperCase() === "VOID" && num(o.balance_due) <= 0;
                const cancelActionComplete = isCancelled && isFinanciallyVoided;
                return (
                  <tr key={`${o.order_id}-${i}`} className="hover:bg-secondary/30" data-testid={`order-row-${o.order_id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-foreground">{cell(o.order_id)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(o.order_date)}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{cell(o.order_customer_name)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{phone(o.customer_phone)}</td>
                    <td className="px-4 py-3">{cell(o.source_channel)}</td>
                    <td className="px-4 py-3">{cell(o.delivery_type)}</td>
                    <td className="px-4 py-3"><Badge value={o.payment_status} /></td>
                    <td className="px-4 py-3"><Badge value={o.order_status} /></td>
                    <td className="px-4 py-3 text-right font-mono">{crc(o.balance_due)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{crc(orderTotal(o))}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          disabled={isCancelled}
                          onClick={() => openAdjust(o)}
                        >
                          <SlidersHorizontal className="h-3.5 w-3.5" /> Ajustar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          disabled={cancelActionComplete}
                          onClick={() => setCancelOrder(o)}
                        >
                          <Ban className="h-3.5 w-3.5" /> {isCancelled ? "Normalizar" : "Cancelar"}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="gap-1.5"
                          onClick={() => openRollback(o)}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Eliminar prueba
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Mostrando {rows.length} de {data.length}</p>
      </StateBlock>

      <Dialog open={Boolean(adjustOrder)} onOpenChange={(open) => !open && closeAdjust()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajustar orden #{adjustOrder?.order_id}</DialogTitle>
            <DialogDescription>
              Corrige precios vendidos, aplica promociones y agrega envío. No modifica el precio maestro del inventario.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Precios vendidos</h3>
              {currentItems.map((item) => (
                <div key={item.order_item_id} className="grid grid-cols-[1fr_110px_90px] items-center gap-3 rounded-lg border p-3">
                  <div>
                    <p className="font-medium">{cell(item.product_name)}</p>
                    <p className="text-xs text-muted-foreground">{cell(item.order_item_id)} · qty {cell(item.qty)}</p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={linePrices[String(item.order_item_id)] ?? ""}
                    onChange={(e) => setLinePrices((p) => ({ ...p, [String(item.order_item_id)]: e.target.value }))}
                    disabled={submitting}
                  />
                  <div className="text-right font-mono text-sm">
                    {crc(num(linePrices[String(item.order_item_id)]) * num(item.qty))}
                  </div>
                </div>
              ))}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Promociones</h3>
              {promotions.map((p) => {
                const id = String(p.promotion_id);
                const checked = selectedPromotions.includes(id);
                return (
                  <label key={id} className="flex items-center justify-between rounded-lg border p-3 cursor-pointer">
                    <span>
                      <span className="font-medium block">{p.promotion_name}</span>
                      <span className="text-xs text-muted-foreground">{p.discount_value}% · {id}</span>
                    </span>
                    <input type="checkbox" checked={checked} onChange={() => togglePromotion(id)} disabled={submitting} />
                  </label>
                );
              })}
              <label className="flex items-start gap-2 rounded-lg bg-secondary/40 p-3 text-sm">
                <input type="checkbox" className="mt-1" checked={sameBase} onChange={(e) => setSameBase(e.target.checked)} disabled={submitting} />
                <span>
                  <strong>Excepción: misma base.</strong> Aplica todas las promociones sobre el subtotal original. Para ventas nuevas debe quedar desactivado; los descuentos se calculan secuencialmente.
                </span>
              </label>
            </section>

            <section className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shipping-amount">Envío</Label>
                <Input id="shipping-amount" type="number" min="0" step="1" value={shippingAmount} onChange={(e) => setShippingAmount(e.target.value)} disabled={submitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adjust-note">Nota</Label>
                <Input id="adjust-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo / referencia" disabled={submitting} />
              </div>
            </section>

            <section className="rounded-xl border bg-secondary/20 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal productos</span><strong>{crc(subtotalPreview)}</strong></div>
              {promotionPreview.details.map((p) => (
                <div key={p.promotion_id} className="flex justify-between text-muted-foreground">
                  <span>{p.promotion_name} ({p.discount_value}% sobre {crc(p.base)})</span>
                  <span>-{crc(p.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between"><span>Descuentos</span><span>-{crc(promotionPreview.discount)}</span></div>
              <div className="flex justify-between"><span>Envío</span><span>{crc(shippingAmount)}</span></div>
              <div className="flex justify-between border-t pt-2 text-base"><strong>Total nuevo</strong><strong>{crc(promotionPreview.total)}</strong></div>
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeAdjust} disabled={submitting}>Cerrar</Button>
            <Button onClick={submitAdjustment} disabled={submitting}>
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</> : "Guardar ajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cancelOrder)} onOpenChange={(open) => !open && !lifecycleSubmitting && setCancelOrder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {String(cancelOrder?.order_status).toLowerCase() === "cancelada"
                ? `Normalizar orden #${cancelOrder?.order_id}`
                : `Cancelar orden #${cancelOrder?.order_id}`}
            </DialogTitle>
            <DialogDescription>
              {String(cancelOrder?.order_status).toLowerCase() === "cancelada"
                ? "La orden ya está cancelada. Esta acción solo normalizará su estado financiero a Sin efecto y saldo cero; no restaurará inventario otra vez."
                : "Esta es una cancelación comercial. La orden y sus líneas permanecerán en el historial, el inventario se restaurará y la orden saldrá de Collections y Operaciones."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-secondary/30 p-3 text-sm">
            Los pagos registrados no se eliminan ni se reembolsan automáticamente. Cualquier devolución financiera se maneja como un proceso separado para conservar trazabilidad.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOrder(null)} disabled={lifecycleSubmitting}>Volver</Button>
            <Button variant="destructive" onClick={submitCancelOrder} disabled={lifecycleSubmitting}>
              {lifecycleSubmitting
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</>
                : String(cancelOrder?.order_status).toLowerCase() === "cancelada"
                  ? "Normalizar estado"
                  : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(rollbackOrder)} onOpenChange={(open) => !open && closeRollback()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar permanentemente la orden #{rollbackOrder?.order_id}</DialogTitle>
            <DialogDescription>
              Esta acción es únicamente para órdenes ficticias de prueba o errores técnicos. El rollback elimina la transacción del historial y restaura sus efectos operativos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
              Esta operación es destructiva. No la uses para una cancelación real de cliente.
            </div>
            <div className="space-y-2">
              <Label htmlFor="rollback-confirm">Escribe {rollbackOrder?.order_id} para confirmar</Label>
              <Input
                id="rollback-confirm"
                value={rollbackConfirmText}
                onChange={(e) => setRollbackConfirmText(e.target.value)}
                placeholder={String(rollbackOrder?.order_id || "")}
                autoComplete="off"
                disabled={lifecycleSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRollback} disabled={lifecycleSubmitting}>Volver</Button>
            <Button
              variant="destructive"
              onClick={submitRollback}
              disabled={lifecycleSubmitting || rollbackConfirmText.trim() !== String(rollbackOrder?.order_id || "")}
            >
              {lifecycleSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Eliminando...</> : "Eliminar definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
